import { IconCheckCircle } from "@/assets/icons/geist/IconCheckCircle";
import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { Button } from "@/components/flexkit/Button";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/modal/Modal";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { Text } from "@/components/podkit/typography/Text";
import {
    useCreateSSOConfiguration,
    useDeleteSSOConfiguration,
    useGetDomainVerification,
    useUpdateSSOConfiguration,
} from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import {
    CreateSSOConfigurationRequest,
    DomainVerificationState,
    UpdateSSOConfigurationRequest,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useMemo, useState, type FC, type FormEvent } from "react";
import { useForm } from "react-hook-form";

export type ConfigureSSOProps = {
    organizationId: string;
    config?: SSOConfiguration;
    onClose?: () => void;
    onVerify?: () => void;
    testValidationOnly?: boolean;
};

type SSOForm = {
    domain: string;
    issuer_url: string;
    client_id: string;
    client_secret: string;
};

type ConfigureSSOState = "configure" | "confirm" | "remove";

export const ConfigureSSO: FC<ConfigureSSOProps> = ({
    organizationId,
    config,
    onClose,
    onVerify,
    testValidationOnly,
}) => {
    const { toast } = useToast();
    const [state, setState] = useState<ConfigureSSOState>("configure");

    const { domainVerification } = useGetDomainVerification(config?.emailDomain);
    const isDomainVerified = domainVerification?.state === DomainVerificationState.VERIFIED;
    const isEdit = !!config;

    const {
        register,
        handleSubmit,
        formState: { errors, isValid, isSubmitting, dirtyFields, isDirty },
        getValues,
    } = useForm<SSOForm>({
        mode: "all",
        defaultValues: isEdit
            ? {
                  domain: config.emailDomain,
                  issuer_url: config.issuerUrl,
                  client_id: config.clientId,
                  client_secret: config.id,
              }
            : undefined,
    });

    const createSSOConfig = useCreateSSOConfiguration();
    const updateSSOConfig = useUpdateSSOConfiguration();
    const deleteSSOConfig = useDeleteSSOConfiguration();

    const [serverError, setServerError] = useState<string>();

    const handleServerErrors = useCallback(
        (error: unknown, _data: SSOForm) => {
            const description = formatError(error);
            setServerError(description);
            toast({
                title: "Failed to create SSO configuration",
                description,
            });
        },
        [toast],
    );

    const handleOnSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            const onValid = async (data: SSOForm) => {
                if (testValidationOnly) {
                    console.warn("Test validation only, not submitting form.");
                    return;
                }
                setServerError(undefined);
                let ssoId = "";
                try {
                    if (isEdit) {
                        ssoId = config.id;
                        await updateSSOConfig.mutateAsync(
                            new UpdateSSOConfigurationRequest({
                                ssoConfigurationId: config.id,
                                clientId: dirtyFields.client_id ? data.client_id : undefined,
                                emailDomain: dirtyFields.domain ? data.domain : undefined,
                                issuerUrl: dirtyFields.issuer_url ? data.issuer_url : undefined,
                                clientSecret: dirtyFields.client_secret ? data.client_secret : undefined,
                            }),
                        );
                    } else {
                        const result = await createSSOConfig.mutateAsync(
                            new CreateSSOConfigurationRequest({
                                organizationId,
                                emailDomain: data.domain,
                                issuerUrl: data.issuer_url,
                                clientId: data.client_id,
                                clientSecret: data.client_secret,
                            }),
                        );
                        ssoId = result.id;
                    }
                } catch (error) {
                    handleServerErrors(error, data);
                    return;
                }
                window.open(`/auth/oidc/start?ssoId=${ssoId}&verify=true`, "_blank");
                setState("confirm");
            };
            await handleSubmit(onValid)(event);
        },
        [
            handleSubmit,
            testValidationOnly,
            isEdit,
            config?.id,
            updateSSOConfig,
            dirtyFields,
            createSSOConfig,
            organizationId,
            handleServerErrors,
        ],
    );

    const callbackURL = useMemo(() => {
        return new URL("/auth/oidc/callback", window.location.origin).toString();
    }, []);

    const onOpenDiscoveryMetadata = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            const url = getValues("issuer_url");
            if (!url) {
                return;
            }
            window.open(`${url}/.well-known/openid-configuration`, "_blank");
        },
        [getValues],
    );

    const onRemove = useCallback(async () => {
        if (!config?.id) {
            return;
        }
        try {
            await deleteSSOConfig.mutateAsync(config.id);
            toast({
                title: "SSO configuration removed",
                description: "The SSO configuration has been removed.",
            });
            onClose?.();
        } catch (error) {
            toast({
                title: "Failed to remove SSO configuration",
                description: formatError(error),
            });
        }
    }, [config?.id, deleteSSOConfig, onClose, toast]);

    if (state === "confirm") {
        return (
            <div>
                <Header />

                <DialogBody className="flex flex-col gap-2">
                    <div className="flex flex-col items-center gap-2 rounded-xl border-[0.5px] bg-surface-primary px-6 py-3 text-base text-content-tertiary">
                        <Heading2>SSO configured successfully!</Heading2>
                        <Text>The authentication test should have opened a new browser tab.</Text>
                        <IconCheckCircle size="lg" className="size-16 text-green-500" />
                        <Text>Make sure to verify your domain.</Text>
                    </div>
                </DialogBody>

                <DialogFooter className="sm:justify-end">
                    <Button type="button" variant="secondary" onClick={() => setState("configure")}>
                        Edit
                    </Button>
                    <div className="flex-grow" />
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Do it later
                    </Button>
                    <Button variant="primary" autoFocus={true} onClick={onVerify}>
                        Verify your domain
                    </Button>
                </DialogFooter>
            </div>
        );
    }
    if (state === "remove") {
        return (
            <div>
                <DialogHeader className="flex flex-col gap-2">
                    <DialogTitle className="font-base text-2xl">Remove SSO configuration</DialogTitle>
                </DialogHeader>

                <DialogBody className="flex flex-col gap-2">
                    <Text>
                        This will delete your SSO configuration. Members will no longer be able to log in using SSO.{" "}
                    </Text>
                    <Text>Are you sure you&apos;d like to delete this configuration?</Text>
                </DialogBody>

                <DialogFooter className="sm:justify-end">
                    <Button type="button" variant="secondary" onClick={() => setState("configure")}>
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={onRemove}>
                        Yes, Remove
                    </Button>
                </DialogFooter>
            </div>
        );
    }

    // the default state is "configure"
    return (
        <div>
            <Header />
            <DialogBody className="my-8 flex justify-between gap-3 overflow-y-auto py-0">
                <form
                    onSubmit={handleOnSubmit}
                    id={"setup-sso-form"}
                    name={"Setup SSO"}
                    className="flex w-full flex-col gap-4"
                >
                    <Text>
                        Find instructions for setting up an OIDC application in the{" "}
                        <ExternalLink href="https://www.gitpod.io/docs/flex/sso">documentation</ExternalLink>.
                    </Text>
                    {serverError && (
                        <div className="flex flex-col gap-2 rounded-xl border border-border-base bg-surface-red px-4 py-3">
                            <div className="text-base font-bold">Failed to update the SSO configuration</div>
                            <div className="text-sm text-content-secondary">{serverError}</div>
                        </div>
                    )}
                    <InputField
                        label="Email domain"
                        id="domain"
                        hint="This is the email domain that people will use to sign in."
                        error={errors?.domain?.message || ""}
                    >
                        {isDomainVerified && isEdit ? (
                            <div className="relative">
                                <div className={"absolute inset-y-0 left-2 flex items-center"}>
                                    <IconCheckFill className="text-content-green" size="base" />
                                </div>
                                <Input className="pl-8 ring-inset" disabled value={config?.emailDomain} />
                            </div>
                        ) : (
                            <Input
                                id="domain"
                                placeholder="acme-corp.com"
                                type="text"
                                {...register("domain", {
                                    pattern: {
                                        value: /^(?!:\/\/)([a-zA-Z0-9-_]{1,63}\.)+[a-zA-Z]{2,6}$/,
                                        message: "Email domain must be a valid domain.",
                                    },
                                    required: {
                                        value: true,
                                        message: "Email domain is required.",
                                    },
                                })}
                            />
                        )}
                    </InputField>
                    <InputField
                        label="Issuer URL"
                        id="issuer_url"
                        hint={
                            !errors?.issuer_url?.message && (
                                <span>
                                    Check the{" "}
                                    <ExternalLink href={``} className="text-sm" onClick={onOpenDiscoveryMetadata}>
                                        OIDC Discovery metadata
                                    </ExternalLink>{" "}
                                    of your identity provider.
                                </span>
                            )
                        }
                        error={errors?.issuer_url?.message || ""}
                    >
                        <Input
                            id="issuer_url"
                            placeholder="https://acme-corp.okta.com/app-123"
                            {...register("issuer_url", {
                                pattern: {
                                    value: /^https:\/\/(?:[a-zA-Z0-9.-]+|\d{1,3}(?:\.\d{1,3}){3})(?::\d{1,5})?(?:\/[a-zA-Z0-9._~-]*)*\/?$/,
                                    message: "Must be a valid URL starting with https://.",
                                },
                                required: {
                                    value: true,
                                    message: "Issuer URL is required.",
                                },
                            })}
                        />
                    </InputField>
                    <InputField label="Client ID" id="client_id" error={errors?.client_id?.message || ""}>
                        <Input
                            id="client_id"
                            {...register("client_id", {
                                required: {
                                    value: true,
                                    message: "Client ID is required.",
                                },
                            })}
                        />
                    </InputField>
                    <InputField label="Client secret" id="client_secret" error={errors?.client_secret?.message || ""}>
                        <Input
                            id="client_secret"
                            type="password"
                            {...register("client_secret", {
                                required: {
                                    value: true,
                                    message: "Client secret is required.",
                                },
                            })}
                        />
                    </InputField>
                    <InputField
                        label="Callback URL"
                        id="callback_url"
                        hint="Copy and paste this URL into the OIDC app of your identity provider."
                    >
                        <CopyableInput id="callback_url" readOnly value={callbackURL} />
                    </InputField>
                </form>
            </DialogBody>
            <DialogFooter className="flex flex-row">
                {isEdit && (
                    <Button variant="destructive" onClick={() => setState("remove")}>
                        Remove
                    </Button>
                )}
                <div className="flex-grow" />
                <DialogClose asChild>
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                </DialogClose>
                <Button
                    autoFocus={true}
                    aria-label="test-and-continue"
                    type="submit"
                    data-testid="test-and-continue-button"
                    form="setup-sso-form"
                    variant="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !(isDirty || isEdit)}
                >
                    Save & Test
                </Button>
            </DialogFooter>
        </div>
    );
};

const Header: FC = () => {
    return (
        <DialogHeader className="flex flex-col gap-2">
            <DialogTitle className="font-base text-2xl">Configure SSO</DialogTitle>
            <DialogDescription className="flex flex-col gap-2">
                Configure OpenID Connect (OIDC) Single Sign-On with your identity provider.
            </DialogDescription>
        </DialogHeader>
    );
};
